<app-expandable-section-box
  [title]="'Tagging'"
  [iconClass]="'icon-tags'"
  [isExpandable]="isExpandable"
  [isExpandedByDefault]="isExpandedByDefault"
>
  <div
    header
    class="flex items-center justify-between w-full pr-2"
    data-testid="taggingHeader"
  >
    <div class="flex items-center space-x-3 text-project-2400">
      <i class="text-inherit icon icon-tags"></i>
      <h3 class="text-sm text-inherit whitespace-nowrap">Tagging</h3>
    </div>
    <app-ai-action-button
      *ngIf="isAutoTaggingEnabled"
      data-testid="autoTagTriggerBtn"
      label="Auto Tag"
      [busyLabel]="autoTagButtonLabel"
      [busy]="isAutoTagBusy"
      [disabled]="!canRunAutoTag"
      [title]="autoTagButtonTitle"
      (run)="onRunAutoTag()"
    ></app-ai-action-button>
  </div>

  <div class="flex flex-col flex-1 py-3" expandedContent>
    <div
      *ngIf="autoTagNoChangeVisible"
      class="flex items-start px-3 py-2 mb-3 text-xs rounded gap-x-2 bg-project-2200 text-project-2400"
      data-testid="autoTagNoChange"
      role="status"
    >
      <app-ai-icon class="mt-0.5 text-sm text-project-300"></app-ai-icon>
      <span>{{ autoTagNoChangeMessage }}</span>
    </div>

    <!-- Auto Tag status box (mirrors smart linking): a busy phase + detail with an animated
         sweep while running, and an error line if the run fails. -->
    <div
      *ngIf="isAutoTagBusy"
      class="relative flex items-start px-3 py-2 mb-3 overflow-hidden text-xs rounded gap-x-2 bg-project-2200"
      data-testid="autoTagBusy"
      [attr.data-phase]="autoTagStatus"
    >
      <app-ai-icon class="mt-0.5 text-sm text-project-300"></app-ai-icon>
      <span class="flex flex-col gap-y-0.5">
        <span
          class="block font-semibold leading-4 text-project-1700"
          data-testid="autoTagBusyPhase"
          >{{ autoTagBusyText }}</span
        >
        <span
          class="block leading-4 text-xxs text-project-2300"
          data-testid="autoTagBusyDetail"
          >{{ autoTagBusyDetail }}</span
        >
      </span>
      <span
        class="absolute bottom-0 left-0 right-0 overflow-hidden h-0.5 bg-project-2900"
        aria-hidden="true"
      >
        <span class="block h-full auto-tag-sweep bg-project-300"></span>
      </span>
    </div>

    <p
      *ngIf="autoTagStatus === 'error'"
      class="flex items-center px-3 py-2 mb-3 text-xs font-medium rounded gap-x-2 bg-project-2200 text-project-2300"
      role="alert"
      data-testid="autoTagError"
    >
      {{ autoTagErrorMessage }}
    </p>

    <p
      *ngIf="autoTagContentChanged"
      class="flex items-start px-3 py-2 mb-3 text-xs rounded gap-x-2 bg-project-2200 text-project-2400"
      data-testid="autoTagContentChanged"
    >
      <i class="mt-0.5 icon icon-info-circle" aria-hidden="true"></i>
      <span
        >The content has changed since it was auto tagged — run Auto Tag again to
        refresh the suggestions.</span
      >
    </p>

    <div
      *ngIf="autoTagReviewItems.length"
      class="mb-3 border rounded border-project-100 bg-project-2200"
      data-testid="autoTagReview"
    >
      <p class="px-2 pt-2 text-xs font-bold text-project-2400">
        <span class="text-red-600" aria-hidden="true">⚠</span> Needs review
      </p>
      <div
        *ngFor="let item of autoTagReviewItems"
        class="px-2 py-2"
        data-testid="autoTagReviewItem"
      >
        <p class="text-xs text-project-2400">
          <span class="uppercase text-project-100">{{ item.kind }}</span>
          &mdash; pick the best match for "{{ item.name }}"
        </p>
        <p *ngIf="item.reason" class="mb-1 text-project-100 text-xxs">
          {{ item.reason }}
        </p>
        <div class="flex flex-wrap gap-1">
          <button
            *ngFor="let option of item.options"
            type="button"
            class="px-2 py-1 text-xs border rounded border-project-100 text-project-2400 hover:text-project-2700"
            [class.italic]="option.id === null"
            (click)="pickReviewOption(item, option)"
            data-testid="autoTagReviewOption"
          >
            {{ option.name }}
          </button>
        </div>
      </div>
    </div>

    <app-search-list
      expandedContent
      [selectedList]="selectedTopicList"
      [searchFormControl]="searchTopicsFormControl"
      [searchResults]="topicSearchResults"
      [isInfoButtonShown]="false"
      [isAddButtonShown]="false"
      [placeholder]="'Topics'"
      [isSurtitleButtonShown]="isShowingSurtitles"
      (handleRemove)="removeTopic($event)"
      (handleAdd)="addTopic($event)"
      (handlePromote)="promoteSurtitle($event, SurtitleTagType.Topic)"
      class="flex flex-col flex-1 w-full pt-1 pb-2"
    ></app-search-list>
    <app-search-list
      expandedContent
      [selectedList]="selectedTagList"
      [searchFormControl]="searchTagsFormControl"
      [searchResults]="tagSearchResults"
      [isInfoButtonShown]="false"
      [isAddButtonShown]="false"
      [placeholder]="'Tags'"
      [isSurtitleButtonShown]="isShowingSurtitles"
      (handleRemove)="removeTags($event)"
      (handleAdd)="addTags($event)"
      (handlePromote)="promoteSurtitle($event, SurtitleTagType.Soft)"
      class="flex flex-col flex-1 w-full pt-1 pb-2"
    ></app-search-list>
    <button
      mat-stroked-button
      class="mx-2 mt-4 mb-2"
      *ngIf="
        !isSearchingTags &&
        tagSearchResults?.length === 0 &&
        searchTagsFormControl.value != ''
      "
      (click)="createSoftTag(searchTagsFormControl.value)"
    >
      <i class="icon icon-plus"></i> Add Soft Tag
    </button>
    <div *ngIf="isShowingPeopleAndCompanies">
      <app-search-list
        expandedContent
        [selectedList]="selectedCompanyList"
        [searchFormControl]="searchCompaniesFormControl"
        [searchResults]="companySearchResults"
        [isInfoButtonShown]="false"
        [isAddButtonShown]="false"
        [placeholder]="'Companies'"
        [isSurtitleButtonShown]="isShowingSurtitles"
        (handleRemove)="removeCompanies($event)"
        (handleAdd)="addCompanies($event)"
        (handlePromote)="promoteSurtitle($event, SurtitleTagType.Company)"
        (handleAddToBody)="addCompanyToContentBody($event)"
        class="flex flex-col flex-1 w-full pt-1 pb-2"
      ></app-search-list>
      <app-search-list
        expandedContent
        [selectedList]="selectedPersonList"
        [searchFormControl]="searchPeopleFormControl"
        [searchResults]="personSearchResults"
        [isInfoButtonShown]="false"
        [isAddButtonShown]="false"
        [placeholder]="'People'"
        [isSurtitleButtonShown]="false"
        (handleRemove)="removePerson($event)"
        (handleAdd)="addPerson($event)"
        class="flex flex-col flex-1 w-full pt-1"
      ></app-search-list>
      <div class="px-1 pt-1">
        <i class="icon icon-info-circle"></i> Data from Investor Exchange. To
        add or amend people / company details - first
        <em>save your content</em>, then
        <a class="cursor-pointer" (click)="sendDqaEmail()"
          >send details here.</a
        >
      </div>
    </div>
  </div>

  <div class="flex flex-col flex-1" notExpandedContent>
    <div
      class="flex flex-col w-full py-2 pr-3 m-0 text-xs bg-project-2200"
      *ngIf="topicControl.value?.length"
      data-testid="topic"
    >
      <p class="text-xs font-bold">Topics</p>
      <ng-container *ngFor="let selectedItem of selectedTopicList">
        <p class="w-full py-px text-project-2400">
          {{ selectedItem.displayName }}
        </p>
      </ng-container>
    </div>

    <div
      class="flex flex-col w-full py-2 pr-3 m-0 bg-project-2200"
      *ngIf="tagControl.value?.length"
      data-testid="tag"
    >
      <p class="text-xs font-bold">Tags</p>
      <ng-container *ngFor="let selectedItem of selectedTagList">
        <p class="w-full py-px text-xs text-project-2400">
          {{ selectedItem.displayName }}
        </p>
      </ng-container>
    </div>

    <div *ngIf="isShowingPeopleAndCompanies">
      <div
        class="flex flex-col w-full py-2 pr-3 m-0 bg-project-2200"
        *ngIf="companyControl.value?.length"
        data-testid="company"
      >
        <p class="text-xs font-bold">Companies</p>
        <ng-container *ngFor="let selectedItem of selectedCompanyList">
          <p class="w-full py-px text-xs text-project-2400">
            {{ selectedItem.displayName }}
          </p>
        </ng-container>
      </div>

      <div
        class="flex flex-col w-full py-2 pr-3 m-0 bg-project-2200"
        *ngIf="peopleControl.value?.length"
        data-testid="people"
      >
        <p class="text-xs font-bold">People</p>
        <ng-container *ngFor="let selectedItem of selectedPersonList">
          <p class="w-full py-px text-xs text-project-2400">
            {{ selectedItem.displayName }}
          </p>
        </ng-container>
      </div>
    </div>
  </div>
</app-expandable-section-box>
